// src/components/calendar/_grid-geometry.js — CHANTIER CALENDRIER RÔLES, Lot B-core.
//
// Helpers de géométrie PURS d'une grille-mois (semaines lun→dim), factorisés
// du comportement EXISTANT et validé en prod :
//   - ProprioCalendrierView.jsx → buildGrid / weekBars (mode "spans")
//   - MenageCalendrierView.jsx  → buildMonthGrid              (mode "badges")
//
// Aucune dépendance React/DOM/réseau/métier ici : que des dates et des entiers.
// Le packing/positionnement reproduit À L'IDENTIQUE celui de buildGrid+weekBars
// (Proprio) — voir _grid-geometry.test.js (parité géométrie).

// ISO local (YYYY-MM-DD) à partir des composantes locales de la date. Déterministe
// et indépendant du fuseau (on ne formate pas via Intl/TZ : on lit y/m/d bruts),
// ce qui évite tout décalage de jour observé avec un formatage Paris sur un runner
// CI en fuseau lointain. Identique à `isoOf` de ProprioCalendrierView.
export function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Numéro de jour (1-31) depuis un ISO, sans reparser de Date.
export function dayNumOf(iso) {
  return Number(String(iso).slice(8, 10))
}

// Week-end (samedi/dimanche) depuis un ISO. Midi local pour éviter tout bord DST.
export function isWeekendIso(iso) {
  const wd = new Date(`${iso}T12:00:00`).getDay()
  return wd === 0 || wd === 6
}

// Grille du mois en semaines complètes. Chaque semaine = 7 cellules { iso, inMonth }.
// weekStartsMonday=true (défaut) reproduit le comportement existant : offset
// lundi = (jsDay + 6) % 7. Le mode dimanche (offset = jsDay) est fourni pour
// l'API mais non utilisé par les vues actuelles.
// Condition d'arrêt `cur <= monthEnd` identique à buildGrid (Proprio) : on émet
// une semaine tant que son lundi (cur en début de boucle) est ≤ dernier jour du
// mois → nombre de semaines = ceil((offset + nbJours) / 7), comme MenageCalendrier.
export function buildMonthGrid(year, month1, weekStartsMonday = true) {
  const first = new Date(year, month1 - 1, 1)
  const jsDay = first.getDay() // 0=dim … 6=sam
  const offset = weekStartsMonday ? (jsDay + 6) % 7 : jsDay
  const cur = new Date(year, month1 - 1, 1 - offset)
  const monthEnd = new Date(year, month1, 0)
  const weeks = []
  while (cur <= monthEnd) {
    const week = []
    for (let d = 0; d < 7; d++) {
      week.push({ iso: isoOf(cur), inMonth: cur.getMonth() === month1 - 1 })
      cur.setDate(cur.getDate() + 1)
    }
    weeks.push(week)
  }
  return weeks
}

// Lane-packing intra-semaine des spans (barres de séjour continues). Reproduit
// EXACTEMENT weekBars de ProprioCalendrierView :
//   - une nuit couverte ⇔ startIso <= iso < endIso (endIso EXCLUSIF).
//   - cs/ce = première/dernière colonne (0-6) couverte dans la semaine.
//   - openL/openR : la barre déborde à gauche/droite de la semaine.
//   - tri (cs, ce) puis placement glouton sur la 1re lane libre.
// Entrée : segments [{ startIso, endIso, … }], weekDays [{ iso }, … 7].
// Sortie : { bars: [{ seg, cs, ce, lane, openL, openR }], laneCount } (≥ 1).
export function weekBars(segments, weekDays) {
  const w0 = weekDays[0].iso
  const w6 = weekDays[6].iso
  const bars = []
  for (const seg of segments) {
    if (!seg || !seg.startIso || !seg.endIso || seg.endIso <= seg.startIso) continue
    let cs = -1
    let ce = -1
    for (let i = 0; i < 7; i++) {
      const iso = weekDays[i].iso
      if (seg.startIso <= iso && iso < seg.endIso) {
        if (cs === -1) cs = i
        ce = i
      }
    }
    if (cs === -1) continue
    bars.push({ seg, cs, ce, openL: seg.startIso < w0, openR: seg.endIso > w6, lane: 0 })
  }
  bars.sort((a, b) => a.cs - b.cs || a.ce - b.ce)
  const lanes = []
  for (const b of bars) {
    let placed = false
    for (let L = 0; L < lanes.length; L++) {
      if (lanes[L] < b.cs) {
        b.lane = L
        lanes[L] = b.ce
        placed = true
        break
      }
    }
    if (!placed) {
      b.lane = lanes.length
      lanes.push(b.ce)
    }
  }
  return { bars, laneCount: Math.max(lanes.length, 1) }
}

// Spans couvrant un jour donné (start <= iso < end, end EXCLUSIF). Sert le point
// d'extension renderDaySlot en mode "spans" (itemsOfDay).
export function segmentsCoveringDay(segments, iso) {
  return (segments || []).filter(
    (seg) => seg && seg.startIso && seg.endIso && seg.startIso <= iso && iso < seg.endIso,
  )
}
